import * as models from 'smg/customer-api/models';
import { CustomizerEngine, HtmlTransformer, SerializedLayer, TextLayer } from 'smg/customizer';
import { BuilderTextDropUI } from 'smg/customizer/text-drop';
import { FONT_NAMES, FontHasFace, RegisterDropdownFaces, ResolveFontFace, ResolveSerializedTextStyle } from '../product-customizer/fonts';
import { CanvasSize, ShapeClipFunc } from './geometry';

// Below lg the pin is small and pinned in a half-height preview, with no room beside a
// block for the floating toolbar — it docks as a sheet under the preview instead
const SHEET_TOOLBAR_QUERY = '(max-width: 991.98px)';
// From lg a real-size pin is small — under the block is on the pin, so the toolbar
// floats beside it, wrapping to fit the stage's side room
const BESIDE_TOOLBAR_MAX_WIDTH_PX = 256;
// At actual size a default block is about 48 px wide, and a bend handle on its peak
// would cover the whole of it — under this width the handle rides just below the block
const BEND_HANDLE_BELOW_BLOCKS_NARROWER_THAN_PX = 96;

/**
 * Free-position text blocks on the pin builder — the single-side counterpart to the coin
 * builder's TextDropUI. The shared BuilderTextDropUI core owns blocks/selection/actions;
 * this subclass owns PinImageUploadUI's lifecycle: the page recreates the engine on
 * every shape/size switch, so blocks are held by serialized value and rebuilt via
 * attachTo(), rescaled when the new shape/size has a different canvas.
 */
export class PinTextDropUI extends BuilderTextDropUI<null> {
    #engine: CustomizerEngine | null = null;
    #currentSize: models.LineItemTypeSize | null = null;
    #transformer: HtmlTransformer | null = null;

    constructor() {
        super({
            fonts: FONT_NAMES,
            resolveFontFace: ResolveFontFace,
            fontHasFace: FontHasFace,
            changedEventName: 'pin-text-drop-changed',
            protectedSelectors:
                '#coin-preview-container, ' +
                '.customizer-text-toolbar, .customizer-bend-handle, .html-transformer-overlay, ' +
                '#pin-design-uploads, .design-layer-row, .layer-action-bar, .add-text-drop-btn, .add-text-btn',
            duplicateOffsetPx: 30,
            toolbarSheetHost: () => {
                if (!window.matchMedia(SHEET_TOOLBAR_QUERY).matches) {
                    return null;
                }
                return document.getElementById('pin-text-toolbar-sheet');
            },
            toolbarBesideMaxWidth: BESIDE_TOOLBAR_MAX_WIDTH_PX,
            bendHandleBelowBlocksNarrowerThanPx: BEND_HANDLE_BELOW_BLOCKS_NARROWER_THAN_PX,
        });
        // The toolbar's font menu renders each option in its own face; pins has
        // no ring-text UI to register them (coins/ribbon do it in theirs)
        RegisterDropdownFaces();
    }

    protected override currentTag(): null {
        return null;
    }

    protected override engineFor(): CustomizerEngine | null {
        return this.#engine;
    }

    protected override transformerFor(): HtmlTransformer | null {
        return this.#transformer;
    }

    protected override allTransformers(): HtmlTransformer[] {
        return this.#transformer ? [this.#transformer] : [];
    }

    // Blocks clip to the current shape's printable area like uploads do
    protected override applyClip(layer: TextLayer): void {
        if (this.#currentSize) {
            layer.setClipFunc(ShapeClipFunc(this.#currentSize));
        }
    }

    protected override blockCenter(): { x: number; y: number } | null {
        if (!this.#currentSize) {
            return null;
        }
        const canvas = CanvasSize(this.#currentSize);
        return { x: canvas.width / 2, y: canvas.height / 2 };
    }

    // Nothing covers the pin (the empty pin is its own upload target), so new blocks
    // land dead center
    protected override spawnPosition(): { x: number; y: number } | null {
        if (!this.#currentSize) {
            return null;
        }
        const canvas = CanvasSize(this.#currentSize);
        return { x: canvas.width / 2, y: canvas.height / 2 };
    }

    // New blocks go on top of previously-restored uploads (their z interleave is
    // rebuilt bottom-up on every engine rebuild)
    protected override afterBlockAdded(id: string, tag: null, engine: CustomizerEngine): void {
        engine.bringLayerToFront(id);
    }

    /**
     * Bind to a freshly-created engine after a shape/size switch: recreates the
     * transformer, rebuilds every stored block from its serialized snapshot (rescaled
     * to the new canvas), and re-applies the printable-area clip for the new shape.
     */
    async attachTo(engine: CustomizerEngine, sizeInit: models.ILineItemTypeSize): Promise<void> {
        const size = new models.LineItemTypeSize(sizeInit);
        this.#transformer?.detach();
        this.#engine = engine;
        this.#currentSize = size;
        this.#transformer = new HtmlTransformer(engine, {}, { rotationSnap: true });
        this.#transformer.hide();

        this.hookEngine(engine);

        // Ascending captured z-order, so interleave with uploads reconstructs correctly
        // (PinImageUploadUI re-adds its images the same way)
        const ordered = [...this.blockMap.entries()].
            sort((a, b) => (a[1].serialized?.zIndex ?? 0) - (b[1].serialized?.zIndex ?? 0));
        for (const [id, block] of ordered) {
            if (!block.serialized) {
                continue;
            }
            await this.#buildLayer(this.#rescaled(block.serialized, block.canvas));
            this.wireSelection(id);
        }
    }

    /** Snapshot every block's live state into the map before the engine is torn down */
    captureTransforms(): void {
        if (!this.#engine || !this.#currentSize) {
            return;
        }
        for (const block of this.blockMap.values()) {
            // Seeded-but-never-built blocks (no node yet) already hold their pending
            // snapshot — serializing the placeholder would wipe it
            if (!block.layer.getTextNode()) {
                continue;
            }
            block.serialized = block.layer.serialize();
            block.canvas = CanvasSize(this.#currentSize);
        }
        this.deselectAll();
    }

    /** A serialized snapshot with position/scale mapped onto the current canvas size */
    #rescaled(snapshot: SerializedLayer, fromCanvas?: { width: number; height: number }): SerializedLayer {
        const serialized: SerializedLayer = { ...snapshot };
        if (!this.#currentSize || !fromCanvas) {
            return serialized;
        }
        const canvas = CanvasSize(this.#currentSize);
        const ratioX = canvas.width / fromCanvas.width;
        const ratioY = canvas.height / fromCanvas.height;
        if (serialized.position) {
            serialized.position = { x: serialized.position.x * ratioX, y: serialized.position.y * ratioY };
        }
        if (serialized.scale) {
            // Uniform, keyed to width like uploads — text mustn't stretch anisotropically
            serialized.scale = { x: serialized.scale.x * ratioX, y: serialized.scale.y * ratioX };
        }
        return serialized;
    }

    /** Create + attach an engine layer from a serialized snapshot (attach/restore path) */
    async #buildLayer(serialized: SerializedLayer): Promise<TextLayer> {
        const layer = new TextLayer(serialized.id);
        await layer.deserialize(serialized);
        this.blockMap.set(serialized.id, { layer, tag: null });
        await this.#engine?.addLayer(serialized.id, layer);
        this.applyClip(layer);
        return layer;
    }

    hasBlocks(): boolean {
        return this.blockMap.size > 0;
    }

    /** Blocks for the layer list (order unspecified — the list sorts by actual z-index) */
    blocks(): { id: string; layer: TextLayer }[] {
        const rows: { id: string; layer: TextLayer }[] = [];
        for (const [id, block] of this.blockMap) {
            rows.push({ id, layer: block.layer });
        }
        return rows;
    }

    /** Trimmed text of visible, non-empty blocks — what actually prints */
    visibleBlockTexts(): string[] {
        const texts: string[] = [];
        for (const block of this.blockMap.values()) {
            if (!block.layer.isVisible()) {
                continue;
            }
            const text = block.layer.getText().trim();
            if (text) {
                texts.push(text);
            }
        }
        return texts;
    }

    /** Whether the pin has printable text content (visible blocks with actual text) */
    hasVisibleContent(): boolean {
        return this.visibleBlockTexts().length > 0;
    }

    /** Serialized blocks, stored in the design's extra state (persistence) */
    getState(): SerializedLayer[] {
        const state: SerializedLayer[] = [];
        for (const block of this.blockMap.values()) {
            state.push(block.layer.serialize());
        }
        return state;
    }

    /**
     * Rebuild blocks from saved state (restore path — blocks are dynamic layers).
     * Before the first attachTo() this just seeds the map — the coming attachTo builds
     * the layers exactly like any engine rebuild. savedCanvas is the canvas px the
     * state was captured on, so positions rescale if the restored size differs (e.g.
     * the saved size no longer exists and the page fell back to a default).
     */
    async restoreState(state: SerializedLayer[], savedCanvas?: { width: number; height: number }): Promise<void> {
        this.deselectAll();
        for (const id of [...this.blockMap.keys()]) {
            this.performDelete(id);
        }

        // Ascending saved z-order so stacking reconstructs correctly
        const sorted = [...state].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
        // Legacy saves may name retired device fonts — swap in the catalog faces.
        // Resolved in parallel (attach order below must stay sequential for z-order)
        const resolved = await Promise.all(sorted.map(saved => ResolveSerializedTextStyle(saved)));
        for (const serialized of resolved) {
            if (this.#engine && this.#currentSize) {
                await this.#buildLayer(this.#rescaled(serialized, savedCanvas));
                this.wireSelection(serialized.id);
            } else {
                this.blockMap.set(serialized.id, { layer: new TextLayer(serialized.id), tag: null, serialized, canvas: savedCanvas });
            }
            this.seedCounterFromId(serialized.id);
        }
        this.notifyChanged();
    }
}
